// Feature Flag Configuration System
// This file manages all features and their rollout across different clients

export const FEATURES = {
  // Core features (always enabled for stability)
  FINANCE_MODULE: 'finance_module',
  MESSAGES_MODULE: 'messages_module',
  TENANT_PORTAL: 'tenant_portal',
  
  // New features with controlled rollout
  AUTOMATED_ALERTS: 'automated_alerts',
  BRANCHES: 'branches',
  INSPECTIONS: 'inspections',
  LANDLORD_LOGIN: 'landlord_login',
  MAINTENANCE_AUTO_REMINDERS: 'maintenance_auto_reminders',
  GLM_GAS_CERT_OCR: 'glm_gas_cert_ocr',
};

// Feature rollout configuration per client
export const FEATURE_ROLLOUT_CONFIG = {
  // Core features - always enabled
  [FEATURES.FINANCE_MODULE]: {
    'demo-plutoo': true,
    'tog-plutoo': true,
    'plutoo': true,
    rolloutPercentage: 100,
    description: 'Financial management including rent tracking, payment processing, arrears monitoring, and financial reports',
    stable: true,
  },
  
  [FEATURES.MESSAGES_MODULE]: {
    'demo-plutoo': true,
    'tog-plutoo': true,
    'plutoo': true,
    rolloutPercentage: 100,
    description: 'Internal messaging system for communication between agents, landlords, tenants, and contractors',
    stable: true,
  },
  
  [FEATURES.TENANT_PORTAL]: {
    'demo-plutoo': true,
    'tog-plutoo': true,
    'plutoo': true,
    rolloutPercentage: 100,
    description: 'Self-service portal for tenants',
    stable: true,
  },
  
  // New features with controlled rollout
  [FEATURES.AUTOMATED_ALERTS]: {
    'demo-plutoo': true,
    'tog-plutoo': false,
    'plutoo': false,
    rolloutPercentage: 0,
    description: 'Automated email alerts for payments in arrears sent to agents (requires Finance Module)',
    stable: false,
  },
  
  [FEATURES.BRANCHES]: {
    'demo-plutoo': true,
    'tog-plutoo': true,
    'plutoo': true,
    rolloutPercentage: 100,
    description: 'Multi-branch management with agent assignment and data isolation',
    stable: true,
  },
  
  [FEATURES.INSPECTIONS]: {
    'demo-plutoo': true,
    'tog-plutoo': true,
    'plutoo': true,
    rolloutPercentage: 100,
    description: 'Property inspection scheduling, tracking, and reporting system',
    stable: true,
  },
  
  [FEATURES.LANDLORD_LOGIN]: {
    'demo-plutoo': true,
    'tog-plutoo': true,
    'plutoo': true,
    rolloutPercentage: 100,
    description: 'Allow landlords to login to the webapp (disabling this prevents all landlord access)',
    stable: true,
  },
  
  [FEATURES.MAINTENANCE_AUTO_REMINDERS]: {
    'demo-plutoo': true,
    'tog-plutoo': true,
    'plutoo': true,
    rolloutPercentage: 100,
    description: 'Automatic reminder emails to tenants for ongoing maintenance requests (sent at day 3, 6, and 9)',
    stable: true,
  },

  [FEATURES.GLM_GAS_CERT_OCR]: {
    'demo-plutoo': true,
    'tog-plutoo': true,
    'plutoo': true,
    'sgh-plutoo': true,
    rolloutPercentage: 100,
    description: 'Gas certificate OCR: analyze uploaded gas certs for address match, Gas ID, and auto-fill expiry date',
    stable: true,
  },
};

// Helper function to get all features for a client
export const getClientFeatures = (clientName) => {
  const enabledFeatures = {};
  
  Object.keys(FEATURE_ROLLOUT_CONFIG).forEach(feature => {
    const config = FEATURE_ROLLOUT_CONFIG[feature];
    enabledFeatures[feature] = config[clientName] || false;
  });
  
  return enabledFeatures;
};

// Display names for feature cards (no vendor/model names)
const FEATURE_DISPLAY_NAMES = {
  [FEATURES.GLM_GAS_CERT_OCR]: 'Gas certificate OCR',
};

// Helper function to get user-facing feature name for cards
export const getFeatureCardDisplayName = (featureValue, featureKey) => {
  if (featureValue != null && FEATURE_DISPLAY_NAMES[featureValue]) return FEATURE_DISPLAY_NAMES[featureValue];
  const raw = featureKey ?? featureValue;
  if (raw == null || typeof raw !== 'string') return 'Feature';
  return raw.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

// Helper function to get feature description
export const getFeatureDescription = (featureName) => {
  return FEATURE_ROLLOUT_CONFIG[featureName]?.description || 'No description available';
};

// Helper function to check if feature is stable
export const isFeatureStable = (featureName) => {
  return FEATURE_ROLLOUT_CONFIG[featureName]?.stable || false;
}; 